import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { Products } from 'src/app/models/Product.model';
import { ProductsService } from 'src/app/services/products.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * ProductDetailComponent — inventory product detail.
 *
 * Reached via two routes (kept for backward-compat with bookmarks):
 *   • /products/:productId
 *   • /products/:family/:subfamily/:group/:subgroup/:id
 *
 * Visually rendered as the Diakonix modal pattern: full-viewport
 * backdrop + centered card with two panes (info sx, posizioni dx).
 * "Chiudi" navigates back to the parent route.
 *
 * Phase 1.5 / v2.7.0 — TS cleaned up:
 *   • dropped kendo-dialog sizing inputs (maxlength, minWidth,
 *     dialogWidth, dialogHeight) — no more kendo-dialog,
 *   • dropped unused @Input fields (order_id, code, isProdOpen,
 *     product_id) — the route param is the single source of truth,
 *   • dropped activeAccordion + onAccordionChange (Ionic-style
 *     code that the template never wired up),
 *   • dropped isZoomed (image zoom feature removed with the
 *     position image cleanup),
 *   • dropped ~6 console.log/debug,
 *   • dropped duplicate OpenModalService injection (was injected
 *     twice as `openSrv` and `closeServ`),
 *   • switch to inject() + takeUntilDestroyed.
 *
 * Behaviour preserved 1:1: loadProduct on route param change,
 * deleteProductPosition Swal confirm + refresh, goToDetails opens
 * the add-position sub-modal.
 */
@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
  standalone: false,
})


export class ProductDetailComponent implements OnInit {

  private readonly productSrv = inject(ProductsService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  public readonly openSrv = inject(OpenModalService);

  // ── Data ────────────────────────────────────────────────────────
  public product: Products.ProductDetails = new Products.ProductDetails(null);
  public pr_position: Products.IProductPosition[] = [];

  // ── UI state ────────────────────────────────────────────────────
  public id = '';
  public loading = false;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.triggerRefresh();
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loader.
  // ──────────────────────────────────────────────────────────────────

  triggerRefresh(): void {
    const fromFamily = this.activatedRoute.snapshot.paramMap.get('family');
    const routeKey = fromFamily ? 'id' : 'productId';
    const raw = this.activatedRoute.snapshot.paramMap.get(routeKey);
    if (!raw) return;

    // The route param is "{slug}-{id}" — the id is the trailing
    // segment after the last '-'.
    this.id = raw.substring(raw.lastIndexOf('-') + 1);
    this.loading = true;

    this.productSrv
      .getProduct(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: Products.IProductDetails) => {
          if (!result) {
            this.loading = false;
            this.cdr.detectChanges();
            return;
          }
          this.product = {
            ...result,
            pr_price: parseFloat(result.pr_price?.toString().replace(',', '.') ?? '0'),
            pr_weight: parseFloat(result.pr_weight?.toString().replace(',', '.') ?? '0'),
            pr_stock: parseFloat(result.pr_stock?.toString().replace(',', '.') ?? '0'),
            committed: parseFloat(result.committed?.toString().replace(',', '.') ?? '0'),
            available: parseFloat(result.available?.toString().replace(',', '.') ?? '0'),
          };
          this.pr_position = this.product.pr_positions ?? [];
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Position actions.
  // ──────────────────────────────────────────────────────────────────

  /** Opens the add-position sub-modal (`product-position`). */
  openAddPosition(): void {
    this.openSrv.onPos();
  }

  /** Confirms + deletes a warehouse position. */
  deleteProductPosition(pr_id: string | number): void {
    if (!pr_id) return;
    this.productSrv
      .deleteProduct(pr_id.toString())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result) {
            Swal.fire({
              icon: 'success',
              title: 'Eliminata!',
              text: 'Posizione eliminata con successo.',
              timer: 1500,
              timerProgressBar: true,
              showConfirmButton: false,
            });
          }
          this.triggerRefresh();
        },
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Close.
  // ──────────────────────────────────────────────────────────────────

  goBack(): void {
    this.router.navigate(['../'], { relativeTo: this.activatedRoute });
  }

  close(): void {
    this.goBack();
  }

  // ──────────────────────────────────────────────────────────────────
  // Helpers.
  // ──────────────────────────────────────────────────────────────────

  isValidNumber(val: unknown): boolean {
    return (
      val !== null &&
      val !== undefined &&
      val !== '' &&
      !isNaN(Number(val))
    );
  }
  
}
